import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BranchPayerDto, BranchPayerPageDto } from '@his/shared';
import { del, get, post, put, qs } from '@/lib/api';
import { useWorkingBranchId } from '@/lib/branch';
import { describeError, useAuth } from '@/lib/auth';
import { DataTable, Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import type { Payer } from '../PayerFormPage';

interface PlanOption {
  id: string;
  name: string;
}

const PAGE_SIZE = 100;

function BranchPayerDialog({ payer, row, branchId, onClose }: { payer: Payer; row: BranchPayerDto | null; branchId: string; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [active, setActive] = useState(row?.active ?? true);
  const [cchiNo, setCchiNo] = useState(row?.cchiNo ?? '');
  const [licence, setLicence] = useState(row?.nphiesLicenseOverride ?? '');
  const [planId, setPlanId] = useState(row?.defaultPlanId ?? '');
  const plans = useQuery({ queryKey: ['payer-plans', payer.id], queryFn: () => get<PlanOption[]>(`/insurance/payers/${payer.id}/plans`), retry: false });

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['branch-payers'] });
    void queryClient.invalidateQueries({ queryKey: ['payers'] });
    onClose();
  };

  const save = useMutation({
    mutationFn: () =>
      row
        ? put<BranchPayerDto>(`/insurance/branch-payers/${row.id}`, { active, cchiNo: cchiNo.trim() || null, nphiesLicenseOverride: licence.trim() || null, defaultPlanId: planId || null })
        : post<BranchPayerDto>('/insurance/branch-payers', {
            branchId,
            payerId: payer.id,
            active,
            ...(cchiNo.trim() ? { cchiNo: cchiNo.trim() } : {}),
            ...(licence.trim() ? { nphiesLicenseOverride: licence.trim() } : {}),
            ...(planId ? { defaultPlanId: planId } : {}),
          }),
    onSuccess: () => {
      toast.success(tr('billing', 'nx_branch_payer_saved'));
      done();
    },
    onError: (caught) => toast.error(tr('billing', 'nx_could_not_save_branch_payer'), describeError(caught)),
  });

  const remove = useMutation({
    mutationFn: () => del(`/insurance/branch-payers/${row?.id}`),
    onSuccess: () => {
      toast.success(tr('billing', 'nx_branch_payer_removed'));
      done();
    },
    onError: (caught) => toast.error(tr('billing', 'nx_could_not_save_branch_payer'), describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('billing', 'nx_branch_payer_title', { payer: payer.name })}
      description={tr('billing', 'nx_branch_payer_description')}
      footer={
        <>
          {row && (
            <Button variant="ghost" className="me-auto" loading={remove.isPending} onClick={() => remove.mutate()}>
              {tr('billing', 'remove')}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            {tr('billing', 'cancel')}
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            {tr('billing', 'save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {!row && (
          <div className="sm:col-span-2">
            <Alert variant="warning">{tr('billing', 'nx_branch_restrict_warning')}</Alert>
          </div>
        )}
        <div className="sm:col-span-2">
          <Checkbox checked={active} onCheckedChange={setActive} label={tr('billing', 'nx_offered_at_this_branch')} />
        </div>
        <Field label={tr('billing', 'nx_cchi_number')}>
          <Input value={cchiNo} maxLength={40} onChange={(event) => setCchiNo(event.target.value)} />
        </Field>
        <Field label={tr('billing', 'nx_licence_override')} hint={tr('billing', 'nx_licence_override_hint')}>
          <Input value={licence} maxLength={60} onChange={(event) => setLicence(event.target.value)} />
        </Field>
        <Field label={tr('billing', 'nx_default_plan')} className="sm:col-span-2">
          <Select value={planId} onChange={(event) => setPlanId(event.target.value)}>
            <option value="">{tr('billing', 'nx_no_default_plan')}</option>
            {plans.data?.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Dialog>
  );
}

export function BranchPayersPanel() {
  const { can } = useAuth();
  const branchId = useWorkingBranchId();
  const [editing, setEditing] = useState<Payer | null>(null);
  const payers = useQuery({ queryKey: ['payers', 'all'], queryFn: () => get<Payer[]>('/insurance/payers?includeInactive=true') });
  const rowsQuery = useInfiniteQuery({
    queryKey: ['branch-payers', branchId],
    initialPageParam: '',
    queryFn: ({ pageParam }) => get<BranchPayerPageDto>(`/insurance/branch-payers${qs({ branchId, limit: PAGE_SIZE, cursor: pageParam || undefined })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: Boolean(branchId),
  });
  const rows = rowsQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const byPayer = new Map(rows.map((row) => [row.payerId, row]));
  const restrictedElsewhere = (payerId: string) => !byPayer.has(payerId);
  const mayManage = can('payer.manage');

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-sm">{tr('billing', 'nx_branch_payers_title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">{tr('billing', 'nx_branch_payers_hint')}</p>
        <DataTable
          dense
          rows={payers.data}
          loading={payers.isLoading || rowsQuery.isLoading}
          error={payers.error ?? rowsQuery.error}
          rowKey={(payer) => payer.id}
          empty={tr('billing', 'nx_no_payers')}
          columns={[
            { header: tr('billing', 'nx_payer_code'), cell: (payer) => <span className="font-mono text-xs">{payer.code}</span> },
            { header: tr('billing', 'payer'), cell: (payer) => payer.name },
            {
              header: tr('billing', 'status'),
              cell: (payer) => {
                const row = byPayer.get(payer.id);
                if (!row) return <Badge variant="secondary">{tr('billing', 'nx_not_set_up_here')}</Badge>;
                return row.active ? <Badge variant="success">{tr('billing', 'nx_offered')}</Badge> : <Badge variant="muted">{tr('billing', 'nx_not_offered')}</Badge>;
              },
            },
            { header: tr('billing', 'nx_cchi_number'), cell: (payer) => byPayer.get(payer.id)?.cchiNo ?? '—' },
            { header: tr('billing', 'nx_licence_override'), cell: (payer) => byPayer.get(payer.id)?.nphiesLicenseOverride ?? '—' },
            { header: tr('billing', 'nx_default_plan'), cell: (payer) => byPayer.get(payer.id)?.defaultPlanName ?? '—' },
            {
              header: '',
              align: 'right',
              cell: (payer) =>
                mayManage && (
                  <Button size="sm" variant="outline" onClick={() => setEditing(payer)}>
                    {restrictedElsewhere(payer.id) ? tr('billing', 'nx_set_up') : tr('billing', 'nx_edit')}
                  </Button>
                ),
            },
          ]}
        />
        {rowsQuery.hasNextPage && (
          <div className="flex justify-center">
            <Button size="sm" variant="outline" loading={rowsQuery.isFetchingNextPage} onClick={() => void rowsQuery.fetchNextPage()}>
              {tr('billing', 'nx_load_more')}
            </Button>
          </div>
        )}
      </CardContent>
      {editing && <BranchPayerDialog payer={editing} row={byPayer.get(editing.id) ?? null} branchId={branchId} onClose={() => setEditing(null)} />}
    </Card>
  );
}
